/*
 * Accessibility fix for data-disable-with elements
 *
 * Elements with data-disable-with are disabled on click, and the text is replaced with alternative text.
 * Since the element is disabled, it is not consistently announced by screen readers.
 *
 * Detect when one of these elements becomes disabled, and announce the alternative text via an aria-live region.
 *
 * This is a global fix for _all_ elements using data-disable-with on the page. The jquery_ujs library, which implements
 * the attribute, does not provide any hooks for extending its functionality, so it needs to be detected separately.
 */

import { onDomReady } from "../../../utils.js";

// Delay in milliseconds for the announcement update
const ANNOUNCEMENT_DELAY = 50;

// Delay in milliseconds for removing the announcement element
const REMOVE_DELAY = 100;

// Weak map of data-disable-with elements to their announcement element
const disableWithAnnouncementElements = new WeakMap();

// Timeout IDs for removal of the announcement element
const announcementElementRemovalTimeoutIds = new WeakMap();

// Timeout IDs for announcement updates
const announcementUpdateTimeoutIds = new WeakMap();

// Helper function to stop a stored timeout
const stopTimeout = (timeoutMap, targetElement) => {
  const oldId = timeoutMap.get(targetElement);
  if (oldId) {
    clearTimeout(oldId);
    timeoutMap.delete(targetElement);
  }
};

const stopElementTimeout = (targetElement) => {
  stopTimeout(announcementElementRemovalTimeoutIds, targetElement);
  stopTimeout(announcementUpdateTimeoutIds, targetElement);
}

const disableWithObserver = new MutationObserver((mutations) => {
  mutations.forEach((record) => {
    stopElementTimeout(record.target);

    if (record.target.disabled) {
      let announcementElement = disableWithAnnouncementElements.get(
        record.target
      );
      if (!announcementElement) {
        announcementElement = document.createElement("div");
        announcementElement.className = "sr-only";
        announcementElement.setAttribute("aria-live", "assertive");
        disableWithAnnouncementElements.set(record.target, announcementElement);

        if (record.target.parentNode) {
          record.target.insertAdjacentElement("afterend", announcementElement);
        } else {
          document.body.appendChild(announcementElement);
        }
      }

      // Delay the announcement update so it gets detected as a change (anything less than 50ms doesn't get announced)
      const newUpdateTimeoutId = setTimeout(() => {
        announcementElement.textContent =
          record.target.getAttribute("data-disable-with");
        announcementUpdateTimeoutIds.delete(record.target);
      }, ANNOUNCEMENT_DELAY);
      announcementUpdateTimeoutIds.set(record.target, newUpdateTimeoutId);
    } else {
      const announcementElement = disableWithAnnouncementElements.get(
        record.target
      );
      if (announcementElement) {
        announcementElement.textContent = "";
        // Remove unneeded announcement element after a short delay
        const newTimeoutId = setTimeout(() => {
          announcementElement.remove();
          announcementElementRemovalTimeoutIds.delete(record.target);
          disableWithAnnouncementElements.delete(record.target);
        }, REMOVE_DELAY);
        announcementElementRemovalTimeoutIds.set(record.target, newTimeoutId);
      }
    }
  });
});

// Helper function to observe an element with data-disable-with
const observeDisableWithElement = (element) => {
  disableWithObserver.observe(element, {
    attributes: true,
    childList: false,
    attributeFilter: ["disabled"],
  });
};

// Helper function to find and observe all data-disable-with elements in a node and its descendants
const observeDisableWithElementsInNode = (node) => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    // Check the node itself
    if (node.hasAttribute("data-disable-with")) {
      observeDisableWithElement(node);
    }
    // Check descendants
    node
      .querySelectorAll("[data-disable-with]")
      .forEach(observeDisableWithElement);
  }
};

onDomReady(() => {
  // Observe each data-disable-with element to detect when it becomes disabled or enabled.
  document
    .querySelectorAll("[data-disable-with]")
    .forEach(observeDisableWithElement);

  // Watch for dynamically added nodes and observe any new data-disable-with elements
  const domObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(observeDisableWithElementsInNode);
    });
  });

  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
